import * as React from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ComposedChart, Bar, Cell } from 'recharts';
import { useFormContext } from 'react-hook-form';
import { useSelectedTokens } from '@/src/hooks';
import { useBondPrice, useTokenizedAssetPools, useCoindeskHistorical } from '@/src/hooks/apis';
import { normalizeCurrencyCode } from '@/src/helpers';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  Spinner,
} from '@/src/ui-components';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/src/ui-components';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/src/ui-components';
import { TradingFormValues } from '../../trading.types';
import type { Currency } from '@/src/hooks/apis';

const bondChartConfig = {
  price: {
    label: 'Price',
    color: 'hsl(var(--primary))',
  },
} satisfies ChartConfig;

const candlestickChartConfig = {
  candle: {
    label: 'Price',
  },
  bullish: {
    label: 'Bullish',
    color: 'hsl(var(--bullish))',
  },
  bearish: {
    label: 'Bearish',
    color: 'hsl(var(--bearish))',
  },
} satisfies ChartConfig;

// Custom candlestick component (renders both wick and body)
const Candlestick = (props: any) => {
  const {
    x,
    y,
    width,
    height,
    low,
    high,
    openClose: [open, close],
  } = props;

  const isGrowing = open < close;
  const color = isGrowing ? 'hsl(var(--bullish))' : 'hsl(var(--bearish))';
  const ratio = Math.abs(height / (open - close));

  const bodyWidth = width * 0.6;
  const bodyX = x + (width - bodyWidth) / 2;
  const centerX = x + width / 2;

  return (
    <g stroke={color} fill={color} strokeWidth="1">
      {/* Candle body */}
      <path
        d={`
          M ${bodyX},${y}
          L ${bodyX},${y + height}
          L ${bodyX + bodyWidth},${y + height}
          L ${bodyX + bodyWidth},${y}
          L ${bodyX},${y}
        `}
      />

      {/* Bottom wick */}
      {isGrowing ? (
        <path
          d={`
            M ${centerX}, ${y + height}
            v ${(open - low) * ratio}
          `}
        />
      ) : (
        <path
          d={`
            M ${centerX}, ${y}
            v ${(close - low) * ratio}
          `}
        />
      )}

      {/* Top wick */}
      {isGrowing ? (
        <path
          d={`
            M ${centerX}, ${y}
            v ${(close - high) * ratio}
          `}
        />
      ) : (
        <path
          d={`
            M ${centerX}, ${y + height}
            v ${(open - high) * ratio}
          `}
        />
      )}
    </g>
  );
};

// Custom tooltip for candlestick
const CandlestickTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;

  const data = payload[0].payload;
  const [open, close] = data.openClose;

  return (
    <div className="rounded-lg border bg-background p-2 shadow-sm">
      <div className="grid gap-2">
        <div className="font-semibold text-xs">{data.date}</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <span className="text-muted-foreground">Open:</span>
          <span className="font-mono">{open.toFixed(6)}</span>

          <span className="text-muted-foreground">High:</span>
          <span className="font-mono text-bullish">{data.high.toFixed(6)}</span>

          <span className="text-muted-foreground">Low:</span>
          <span className="font-mono text-bearish">{data.low.toFixed(6)}</span>

          <span className="text-muted-foreground">Close:</span>
          <span className="font-mono">{close.toFixed(6)}</span>

          {data.volume && (
            <>
              <span className="text-muted-foreground">Volume:</span>
              <span className="font-mono">{data.volume.toLocaleString()}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export function PriceChart() {
  console.log('[PriceChart] Component rendering');

  const { watch } = useFormContext<TradingFormValues>();
  const { baseTokenId, quoteTokenId } = watch();
  const [baseToken, quoteToken] = useSelectedTokens(baseTokenId, quoteTokenId);
  const [currency, setCurrency] = React.useState<Currency>('USD');

  console.log('[PriceChart] Selected tokens:', {
    baseTokenId,
    quoteTokenId,
    baseToken: baseToken?.code,
    quoteToken: quoteToken?.code
  });

  const { data: assetPoolsResponse } = useTokenizedAssetPools();

  // Find the matching asset pool for the selected base token
  const assetPool = React.useMemo(() => {
    if (!assetPoolsResponse?.objects || !baseToken) {
      return null;
    }

    // Match by token currency code or issuer address
    return assetPoolsResponse.objects.find(
      (pool) =>
        pool.xrpl_issuer_address === baseToken.issuer ||
        normalizeCurrencyCode(pool.token_currency_code) === baseToken.code
    );
  }, [assetPoolsResponse, baseToken]);

  const bondId = assetPool?.underlying_bond?.id;
  const hasBond = !!bondId;

  // Check if this is XRP/RLUSD pair (use XRP/USD from CoinDesk as proxy)
  // Support both XRP/RLUSD and RLUSD/XRP
  const isXrpRlusd = React.useMemo(() => {
    // Normalize currency codes (handles hex format like 524C555344... -> RLUSD)
    const baseCode = baseToken?.code ? normalizeCurrencyCode(baseToken.code) : '';
    const quoteCode = quoteToken?.code ? normalizeCurrencyCode(quoteToken.code) : '';

    const hasXrp = baseCode === 'XRP' || quoteCode === 'XRP';
    const hasRlusd = baseCode === 'RLUSD' || quoteCode === 'RLUSD';

    console.log('[PriceChart] Token check:', {
      baseTokenRaw: baseToken?.code,
      quoteTokenRaw: quoteToken?.code,
      baseCode,
      quoteCode,
      hasXrp,
      hasRlusd,
      isXrpRlusd: hasXrp && hasRlusd,
      hasBond
    });

    return hasXrp && hasRlusd;
  }, [baseToken?.code, quoteToken?.code, hasBond]);

  // Fetch bond price data (for tokenized bonds)
  const { data: bondPriceData, isLoading: bondLoading, error: bondError } = useBondPrice({
    bondId,
    currency,
    enabled: hasBond,
  });

  // Fetch XRP/USD data from CoinDesk for XRP/RLUSD pairs
  const { data: coindeskData, isLoading: coindeskLoading, error: coindeskError } = useCoindeskHistorical({
    enabled: !hasBond && isXrpRlusd,
  });

  React.useEffect(() => {
    console.log('[PriceChart] CoinDesk query state:', {
      enabled: !hasBond && isXrpRlusd,
      isLoading: coindeskLoading,
      hasData: !!coindeskData,
      dataLength: coindeskData?.length,
      error: coindeskError
    });
  }, [hasBond, isXrpRlusd, coindeskLoading, coindeskData, coindeskError]);

  const isLoading = hasBond ? bondLoading : coindeskLoading;
  const error = hasBond ? bondError : coindeskError;

  // Transform bond price data for line chart
  const bondChartData = React.useMemo(() => {
    if (!bondPriceData?.objects) {
      return [];
    }

    // Sort by price_date (oldest first) and transform to chart format
    return bondPriceData.objects
      .sort((a, b) => new Date(a.price_date).getTime() - new Date(b.price_date).getTime())
      .map((item) => ({
        date: item.price_date,
        price: parseFloat(item.price),
      }));
  }, [bondPriceData]);

  // CoinDesk data is already in candlestick format
  const candlestickChartData = coindeskData || [];

  const chartData = hasBond ? bondChartData : candlestickChartData;

  // Calculate min/max for candlestick chart domain
  const candlestickDomain = React.useMemo(() => {
    if (candlestickChartData.length === 0) {
      return [0, 1];
    }

    const minValue = candlestickChartData.reduce(
      (minVal, { low, openClose: [open, close] }) => {
        const currentMin = Math.min(low, open, close);
        return minVal === null || currentMin < minVal ? currentMin : minVal;
      },
      null as number | null
    );

    const maxValue = candlestickChartData.reduce(
      (maxVal, { high, openClose: [open, close] }) => {
        const currentMax = Math.max(high, open, close);
        return maxVal === null || currentMax > maxVal ? currentMax : maxVal;
      },
      minValue
    );

    return [minValue ?? 0, maxValue ?? 1] as [number, number];
  }, [candlestickChartData]);

  // Calculate volume domain (10x max volume to make bars ~10% height)
  const volumeDomain = React.useMemo(() => {
    if (candlestickChartData.length === 0) {
      return [0, 1];
    }

    const maxVolume = candlestickChartData.reduce(
      (maxVol, { volume }) => Math.max(maxVol, volume),
      0
    );

    // Set domain to 10x max volume so bars only take ~10% of chart height
    return [0, maxVolume * 10];
  }, [candlestickChartData]);

  React.useEffect(() => {
    console.log('[PriceChart] Chart data:', {
      hasBond,
      isXrpRlusd,
      dataLength: chartData.length,
      firstItem: chartData[0],
      lastItem: chartData[chartData.length - 1],
      candlestickDomain
    });
  }, [chartData, hasBond, isXrpRlusd, candlestickDomain]);

  return (
    <Card className="pt-0 flex-1 flex flex-col">
      <CardHeader className="flex items-center justify-start gap-2 space-y-0 border-b py-3 sm:flex-row">
        <div className="grid flex-1 gap-0.5">
          <CardTitle className="text-sm">Price Chart</CardTitle>
          <CardDescription className="text-xs">
            {baseToken?.description || 'Historical price data'}
          </CardDescription>
        </div>
        {hasBond && (
          <Select value={currency} onValueChange={(value) => setCurrency(value as Currency)}>
            <SelectTrigger
              className="w-[100px] rounded-lg text-xs h-8"
              aria-label="Select currency"
            >
              <SelectValue placeholder="USD" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="USD" className="rounded-lg text-xs">
                USD
              </SelectItem>
              <SelectItem value="AUD" className="rounded-lg text-xs">
                AUD
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </CardHeader>
      <CardContent className="p-2 flex-1 min-h-0">
        {isLoading ? (
          <Empty className="h-full">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Spinner variant="circle" />
              </EmptyMedia>
              <EmptyTitle>Loading price data</EmptyTitle>
            </EmptyHeader>
          </Empty>
        ) : error ? (
          <Empty className="h-full">
            <EmptyHeader>
              <EmptyTitle>Failed to load price data</EmptyTitle>
              <EmptyDescription>Please try again later</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : chartData.length === 0 ? (
          <Empty className="h-full">
            <EmptyHeader>
              <EmptyTitle>No data available</EmptyTitle>
              <EmptyDescription>Reload or Select a different asset.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : hasBond ? (
          <ChartContainer config={bondChartConfig} className="h-full w-full">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="fillPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-price)" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="var(--color-price)" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => `${value.toFixed(2)}`}
              />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => {
                      return new Date(value).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      });
                    }}
                    formatter={(value) => `${currency} ${Number(value).toFixed(4)}`}
                    indicator="dot"
                  />
                }
              />
              <Area
                dataKey="price"
                type="monotone"
                fill="url(#fillPrice)"
                stroke="var(--color-price)"
              />
            </AreaChart>
          </ChartContainer>
        ) : (
          <ChartContainer config={candlestickChartConfig} className="h-full w-full">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="date" className="text-xs" tick={{ fontSize: 10 }} />

              {/* Price Y-axis (left) */}
              <YAxis
                yAxisId="left"
                domain={candlestickDomain}
                className="text-xs"
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => value.toFixed(6)}
              />

              {/* Volume Y-axis (right) */}
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={volumeDomain}
                className="text-xs"
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                  return value.toFixed(0);
                }}
              />

              <ChartTooltip content={<CandlestickTooltip />} />

              {/* Volume bars */}
              <Bar
                yAxisId="right"
                dataKey="volume"
                fill="hsl(var(--muted))"
                opacity={0.3}
                isAnimationActive={false}
              >
                {chartData.map((entry, index) => {
                  const openClose = (entry as any).openClose;
                  const [open, close] = openClose || [0, 0];
                  const color = close >= open ? 'hsl(var(--bullish))' : 'hsl(var(--bearish))';
                  return <Cell key={`cell-${index}`} fill={color} />;
                })}
              </Bar>

              {/* Candlestick bars (both wick and body) */}
              <Bar
                yAxisId="left"
                dataKey="openClose"
                fill="#8884d8"
                shape={<Candlestick />}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
